import React, { memo, useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import AiSparkleIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/AiSparkleIcon';
import {
  buildSegmentsFromResultJson,
  formatSegmentLocation,
} from 'app/shared/modules/LoanApplicationDocuments/AiBatchIntake/AiBatchIntake.utils';
import PropTypes from 'prop-types';

import { DocumentIntakeJobStatus } from 'constants/documentIntake';

import { deriveIntakeDisplayStatus } from 'store/documentIntake';

import { SEGMENT_STATUS_LABEL } from './IntakeProgressDetails.constants';
import {
  drawerContentStyles,
  getStatusBadgeStyles,
  progressHeaderIconStyles,
  progressHeaderInfoStyles,
  progressHeaderStyles,
  progressScrollAreaStyles,
  reviewAffordanceStyles,
  segmentInfoStyles,
  segmentNameStyles,
  segmentRowStyles,
  waitingMessageStyles,
} from './IntakeProgressDetails.styles';
import { IntakeProgressDetailsTestIds } from './IntakeProgressDetails.testIds';

/**
 * Drawer content shown while a document intake job is Processing or when it has
 * Completed.
 *
 * Processing view: shows the overall "Processing — X of Y" progress header plus a
 * live per-segment list derived from job.resultJson (populated as segments complete).
 * The job is polled by IntakeStatusCard so this view updates automatically.
 *
 * Completed view: shows a "Ready to review" affordance with a "Review & file" button
 * that delegates to the parent's onOpenReview callback.
 *
 * DEV-4552: extracted from IntakeStatusCard so the card delegates the two drawer
 * views (progress + review) into a single click target.
 */
const IntakeProgressDetails = ({ job, onOpenReview }) => {
  const onReviewClick = useCallback(() => onOpenReview?.(job), [job, onOpenReview]);

  if (!job) {
    return null;
  }

  const isCompleted = job.status === DocumentIntakeJobStatus.COMPLETED;
  const displayLabel = deriveIntakeDisplayStatus(job);
  const sourceLabel = job.sourceLabel ?? job.uploadLabel ?? null;
  const segmentsTotal = job.totalSegments ?? 0;
  const segmentsProcessed = job.processedSegments ?? 0;

  if (isCompleted) {
    const segmentCountLabel = `${segmentsTotal} document${segmentsTotal === 1 ? '' : 's'}`;
    const readyTitle = sourceLabel
      ? `Ready — ${sourceLabel} (${segmentCountLabel})`
      : `Ready (${segmentCountLabel})`;

    return (
      <Box sx={drawerContentStyles}>
        <Box sx={reviewAffordanceStyles}>
          <Box sx={progressHeaderIconStyles}>
            <AiSparkleIcon size={20} />
          </Box>
          <Typography variant="bold16">{readyTitle}</Typography>
          <Typography color="text.secondary" variant="regular14">
            AI finished processing. Review and file the detected documents below.
          </Typography>
          <Button
            data-testid={IntakeProgressDetailsTestIds.REVIEW_BUTTON}
            variant="contained"
            onClick={onReviewClick}
          >
            Review &amp; file
          </Button>
        </Box>
      </Box>
    );
  }

  // Build a per-segment list from resultJson (populated as the backend processes each segment).
  const segments =
    job.resultJson?.segments?.length > 0 ? buildSegmentsFromResultJson(job.resultJson, job.id) : [];

  const progressSummary =
    segmentsTotal > 0 ? `${segmentsProcessed} of ${segmentsTotal} segments complete` : 'Starting…';

  return (
    <Box sx={drawerContentStyles}>
      <Box sx={progressHeaderStyles}>
        <Box sx={progressHeaderIconStyles}>
          <AiSparkleIcon size={20} />
        </Box>
        <Box sx={progressHeaderInfoStyles}>
          <Typography variant="bold16">{displayLabel || 'Processing…'}</Typography>
          <Typography color="text.secondary" variant="regular12">
            {progressSummary}
            {sourceLabel ? ` · ${sourceLabel}` : ''}
          </Typography>
        </Box>
      </Box>

      <Box sx={progressScrollAreaStyles}>
        {segments.length === 0 && (
          <Box sx={waitingMessageStyles}>
            <Typography color="text.secondary" variant="regular14">
              Waiting for segment details…
            </Typography>
          </Box>
        )}
        {segments.map(seg => {
          const statusText =
            SEGMENT_STATUS_LABEL[seg.serverStatus] ?? seg.serverStatus ?? 'Pending';
          const locationText = formatSegmentLocation(seg);
          const badgeSx = getStatusBadgeStyles(seg.serverStatus);

          return (
            <Box key={seg.id} sx={segmentRowStyles}>
              <Box sx={segmentInfoStyles}>
                <Typography color="text.secondary" sx={segmentNameStyles} variant="regular12">
                  {locationText}
                </Typography>
                {seg.documentTypeId && (
                  <Typography sx={segmentNameStyles} variant="medium14">
                    {seg.documentTypeId}
                  </Typography>
                )}
              </Box>
              <Box sx={badgeSx}>
                <Typography variant="regular12">{statusText}</Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

IntakeProgressDetails.propTypes = {
  job: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    totalSegments: PropTypes.number,
    processedSegments: PropTypes.number,
    sourceLabel: PropTypes.string,
    uploadLabel: PropTypes.string,
    resultJson: PropTypes.shape({
      segments: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  onOpenReview: PropTypes.func,
};

export default memo(IntakeProgressDetails);
